import { Injector, NgModule } from '@angular/core';
import { Config, provideConfig } from 'core/config';
import { RequestOptionsType } from 'core/api/services/api.interface';
import { AuthStore } from '../store/auth.store';
import { HttpHeaders } from '@angular/common/http';
import { UserAuthService } from '../services/user-auth.service';
import { of } from 'rxjs';

declare module 'core/config' {
  interface Config {
    user?: {
      auth?: {
        sessionIdleSeconds?: number;
        inactiveModalTimoutSeconds?: number;
      };
    };
  }
}

export abstract class AuthUserConfig implements Config {
  public user?: {
    auth?: {
      sessionIdleSeconds?: number;
      inactiveModalTimoutSeconds?: number;
    };
  };
}

declare module 'core/config' {
  interface Config extends AuthUserConfig {}
}

@NgModule({
  providers: [
    provideConfig({
      api: {
        endpoints: {
          userDashboard: 'stats/transactions',
          userSelf: 'self',
          roleGroups: 'admin/roleGroups',
          userLogout: { path: 'auth/logout' },
          agentExchangeMint: { path: 'auth/exchange/mint' },
          invoiceDashboardStats: 'stats/invoices',
          invoiceList: 'invoices',
          invoiceDetail: 'invoices/<%= invoiceRef %>/events',
          invoicePaymentEvents: 'invoices/payment-events',
          invoiceFreelyPayments: 'invoices/freely-payments',
          invoiceFreelyPaymentsExport: 'invoices/freely-payments/export',
          invoiceFailedPayments: 'invoices/failed-payments',
          invoiceCustomerOutstanding: 'invoices/customer-outstanding',
          invoiceCustomerBreakdown: 'invoices/customer-breakdown',
          invoiceCustomerSliceInvoices: 'invoices/customer-breakdown/<%= customerId %>/invoices',
          achDashboardStats: 'stats/ach',
          achAvailableRange: 'stats/ach/available-range',
          achDepositSummary: 'stats/ach/deposit-summary',
          achTransactions: 'stats/ach/transactions',
          achTransactionEvents: 'stats/ach/transactions/<%= key %>/events',
          schedulePaymentsStats: 'stats/schedule-payments',
          schedulePaymentsUpcoming: 'stats/schedule-payments/upcoming',
          schedulePaymentsActive: 'stats/schedule-payments/active',
          schedulePaymentsDeclines: 'stats/schedule-payments/declines',
          freelyPaymentHistory: 'payments/history',
          // The A/R dashboard's Unallocated Payments card: the Quick Pay queue and its allocation.
          quickPayUnallocated: 'payments/quick-pay/unallocated',
          quickPayAllocate: 'payments/<%= freelyPaymentId %>/quick-pay/allocate',
          quickPayAllocatePreview: 'payments/<%= freelyPaymentId %>/quick-pay/allocate/preview',
          // The counts behind the AutoPay Eligible card. Without `detail=true`
          // this returns the figures only, not the (potentially thousands of)
          // per-invoice rows the Run wizard reviews.
          autopayEligible: 'admin/profile/<%= profileId %>/autopay/run',
          erpImports: 'integrations/erp/imports',
          erpImportDetail: 'integrations/erp/imports/<%= importDefinitionId %>',
          erpImportRun: { path: 'integrations/erp/imports/<%= importDefinitionId %>/run', headers: {} },
          erpImportExecute: 'integrations/erp/imports/<%= importDefinitionId %>/execute',
          erpImportSchedule: 'integrations/erp/imports/<%= importDefinitionId %>/schedule',
          erpDaySummary: 'integrations/erp/day-summary',
          erpImportHistory: 'integrations/erp/import-history',
          erpImportHistoryDetail: 'integrations/erp/import-history/<%= importId %>',
          erpImportHistoryDetails: 'integrations/erp/import-history/<%= importId %>/details',
          erpImportHistoryDownload: 'integrations/erp/import-history/<%= importId %>/download',
          erpExports: 'integrations/erp/exports',
          erpExportDetail: 'integrations/erp/exports/<%= exportDefinitionId %>',
          erpExportRun: { path: 'integrations/erp/exports/<%= exportDefinitionId %>/run', headers: {} },
          erpExportSchedule: 'integrations/erp/exports/<%= exportDefinitionId %>/schedule',
          erpExport: { path: 'integrations/erp/exports/on-demand', headers: {} },
          erpExportHistory: 'integrations/erp/export-history',
          erpExportDownload: 'integrations/erp/export-history/<%= exportId %>/download',
          erpExportResend: { path: 'integrations/erp/export-history/<%= exportId %>/resend', headers: {} },
  /** The ERP posting ledger — one row per applied document, behind an export or a payment. */
  erpPaymentPostings: 'integrations/erp/payments/postings',
  /** Payments the ERP posting process would select — the same predicate the exporters use. */
  erpEligiblePayments: 'integrations/erp/payments/eligible',
        },
        authFactory: (injector: Injector) => {
          return (options: RequestOptionsType) => {
            const authStore = injector.get(AuthStore);
            options = options ?? {};
            let headers = options?.headers
              ? options.headers instanceof HttpHeaders
                ? options.headers
                : new HttpHeaders(options.headers)
              : new HttpHeaders();

            headers = headers.set('Authorization', `Bearer ${authStore.accessToken()}`);
            options['headers'] = headers;

            return options;
          };
        },

        authRefreshFactory: (injector: Injector) => {
          return () => {
            const userAuthService = injector.get(UserAuthService);
            return userAuthService.refreshToken();
          };
        },

        authFailedFactory: (injector: Injector) => {
          return () => {
            const authStore = injector.get(AuthStore);
            authStore.logout();
            return of(true);
          };
        },
      },
      apiAuth: {
        endpoints: {
          sendMFA: { path: 'auth/sendMFACode', auth: false },
          userLogin: { path: 'auth/login', auth: false },
          userRefreshToken: { path: 'auth/refreshToken' },
          forgotPassword: { path: 'self/forgotPassword', auth: false },
          forgotPasswordReset: { path: 'self/changePassword', auth: false },
        },
      },
      user: {
        auth: {
          sessionIdleSeconds: 600, // 10 minutes
          inactiveModalTimoutSeconds: 60, // 10 seconds
        },
      },
    }),
    { provide: AuthUserConfig, useExisting: Config },
  ],
})
export class ConfigModule {}
